import { useCallback, useEffect, useState } from 'react';
import {
  connectCustomDomain,
  checkCustomDomain,
  disconnectCustomDomain,
  fetchMyCustomDomain,
  normalizeHostname,
  validateCustomHostname,
  type CustomDomainRow,
} from '@/lib/customDomain';

interface UseCustomDomainParams {
  userId?: number;
  userEmail?: string;
}

interface UseCustomDomainResult {
  loading: boolean;
  row: CustomDomainRow | null;
  busy: null | 'connect' | 'verify' | 'disconnect';
  error: string | null;
  message: string | null;
  refresh: () => Promise<void>;
  connect: (hostname: string) => Promise<{ ok: boolean; error?: string }>;
  verify: () => Promise<void>;
  disconnect: () => Promise<void>;
  clearMessages: () => void;
}

/**
 * Hook marchand pour gérer SON domaine personnalisé.
 * Toutes les opérations sensibles passent par l'Edge Function (token serveur).
 */
export function useCustomDomain({ userId, userEmail }: UseCustomDomainParams): UseCustomDomainResult {
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<CustomDomainRow | null>(null);
  const [busy, setBusy] = useState<null | 'connect' | 'verify' | 'disconnect'>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMyCustomDomain(userId);
      setRow(data);
    } catch {
      /* keep previous */
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const clearMessages = useCallback(() => {
    setError(null);
    setMessage(null);
  }, []);

  const connect = useCallback(async (rawHostname: string) => {
    if (!userId || !userEmail) return { ok: false, error: 'Utilisateur non connecté.' };
    setError(null);
    setMessage(null);

    const validationError = validateCustomHostname(rawHostname);
    if (validationError) {
      setError(validationError);
      return { ok: false, error: validationError };
    }

    setBusy('connect');
    try {
      const res = await connectCustomDomain({
        userId,
        userEmail,
        hostname: normalizeHostname(rawHostname),
      });
      if (res.error) {
        setError(res.error);
        return { ok: false, error: res.error };
      }
      await refresh();
      setMessage('Domaine connecté. Configurez le CNAME chez votre registrar puis lancez la vérification.');
      return { ok: true };
    } finally {
      setBusy(null);
    }
  }, [userId, userEmail, refresh]);

  const verify = useCallback(async () => {
    if (!userId || !userEmail) return;
    setError(null);
    setMessage(null);
    setBusy('verify');
    try {
      const res = await checkCustomDomain({ userId, userEmail });
      if (res.error) setError(res.error);
      await refresh();
      if (!res.error) {
        if (res.is_active) setMessage('Domaine actif et sécurisé (SSL validé).');
        else setMessage('Statut mis à jour. La validation peut prendre quelques minutes.');
      }
    } finally {
      setBusy(null);
    }
  }, [userId, userEmail, refresh]);

  const disconnect = useCallback(async () => {
    if (!userId || !userEmail) return;
    setError(null);
    setMessage(null);
    setBusy('disconnect');
    try {
      const res = await disconnectCustomDomain({ userId, userEmail });
      if (res.error) setError(res.error);
      else setMessage('Domaine déconnecté. Votre sous-domaine Zifek reste actif.');
      await refresh();
    } finally {
      setBusy(null);
    }
  }, [userId, userEmail, refresh]);

  return { loading, row, busy, error, message, refresh, connect, verify, disconnect, clearMessages };
}